import React from "react";
import Container from "src/components/common/Layout/Container";
import { Box, Skeleton, Theme, Typography, useMediaQuery } from "@mui/material";
import { colors } from "src/config/colors";
import Link from "src/components/common/Links/Link";
import SubmitButton from "src/components/common/Buttons/SubmitButton";
import ProductInfoList from "src/components/NewProductPage/components/MainProductInfo/components/ProductInfoList";
import { ProductPriceSchema } from "src/models/newProduct/getProductPrice";
import { NewCatalogProductSchema } from "src/models/newProduct/getNewProduct";
import { MainProductInfoSchema } from "src/models/newProduct/getMainProductInfo";
import {
  EnumMetrikActions,
  sendCustomYandexMetrik,
} from "src/config/analytics/sendCustomYandexMetrik";
import { pluralPharmaciesAmount } from "src/utils/helpers/pluralization/pluralPharmaciesAmount";
import SidebarImage from "src/components/NewProductPage/components/SidebarImage";
import { PhotoCaption } from "src/components/NewProductPage/components/RlsNotices";
import Cookies from "js-cookie";
import { DEFAULT_CITY } from "src/utils/constant/constant";
import { createUrl } from "src/utils/helpers/url/createUrl";
import paths from "src/router/paths";
/*import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";*/

interface MainProductInfoProps {
  productPrice?: ProductPriceSchema;
  isLoadingPrice?: boolean;
  productData: NewCatalogProductSchema;
  mainProductInfo?: MainProductInfoSchema[];
  isLoadingProductInfo?: boolean;
  setOpenReservationModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const MainProductInfo: React.FC<MainProductInfoProps> = ({
  productPrice,
  isLoadingPrice,
  productData,
  mainProductInfo,
  isLoadingProductInfo,
  setOpenReservationModal,
}) => {
  const isLargeScreenDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("tb")
  );

  const citySlug = Cookies.get("slug") || DEFAULT_CITY;

  return (
    <Container
      isWithPaper={isLargeScreenDown}
      paddingTop="24px"
      paddingBottom="24px"
      sx={{ width: "100%" }}
      wrapperSx={{ borderRadius: "0px 0px 8px 8px" }}
    >
      <Box
        sx={{
          display: { xs: "none", tb: "block" },
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: colors.black2,
            fontSize: { xs: "18px", md: "28px" },
            lineHeight: { xs: "18px", md: "32px" },
            fontWeight: 500,
            mb: "24px",
          }}
        >
          {productData.main_section.name}
          {productData.main_section.sub_name
            ? `, ${productData.main_section.sub_name}`
            : ""}
        </Typography>
        <Box display="flex" alignItems="center" mb="24px">
          <Box
            sx={{
              borderRadius: "8px",
              border: "1px solid #41983A",
              backgroundColor: productData.main_section.nonPrescriptionDrug
                ? "transparent"
                : colors.green,
              padding: "8px",
              height: "32px",
              width: "fit-content",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: { xs: "12px", md: "16px" },
              color: productData.main_section.nonPrescriptionDrug
                ? colors.green
                : colors.white,
              fontWeight: 400,
              mr: "16px",
            }}
          >
            {productData.main_section.nonPrescriptionDrug
              ? "Без рецепта"
              : "По рецепту"}
          </Box>
          {productData.main_section.catalog_brand_slug && (
            <Link
              sx={{
                color: colors.blue2,
                fontSize: { xs: "12px", tb: "14px" },
              }}
              href={createUrl([
                citySlug,
                paths.CATEGORY,
                productData.main_section.catalog_brand_slug,
              ])}
              onClick={() =>
                sendCustomYandexMetrik(
                  EnumMetrikActions.anotherProductFormBtnOnProductPage
                )
              }
            >
              Все формы выпуска
            </Link>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: {
            xs: "column-reverse",
            tb: "row",
          },
        }}
      >
        <Container
          isWithPaper={!isLargeScreenDown}
          sx={{
            padding: { xs: "16px", tb: "0px", margin: 0 },
          }}
          wrapperSx={{
            width: { xs: "100%", tb: "50%" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            borderRadius: "8px",
          }}
        >
          <ProductInfoList
            mainProductInfo={mainProductInfo}
            isLoadingProductInfo={isLoadingProductInfo}
          />
        </Container>
        <Container
          isWithPaper={!isLargeScreenDown}
          sx={{ padding: { xs: "16px", tb: "0px" }, margin: 0 }}
          wrapperSx={{
            width: { xs: "100%", tb: "50%" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            borderRadius: "8px",
            mb: { xs: 2, tb: 0 },
          }}
        >
          <Box
            sx={{
              display: { xs: "block", tb: "none" },
            }}
          >
            <Typography
              component="h2"
              sx={{
                color: colors.black2,
                fontSize: { xs: "18px", md: "28px" },
                fontWeight: 500,
                mb: "24px",
              }}
            >
              {productData.main_section.name},{" "}
              {productData.main_section.sub_name}
            </Typography>
            <Box display="flex" alignItems="center" mb="24px">
              <Box
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #41983A",
                  backgroundColor: productData.main_section.nonPrescriptionDrug
                    ? "transparent"
                    : colors.green,
                  padding: "8px",
                  height: "32px",
                  width: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "12px", md: "16px" },
                  color: productData.main_section.nonPrescriptionDrug
                    ? colors.green
                    : colors.white,
                  fontWeight: 400,
                  mr: "6px",
                }}
              >
                {productData.main_section.nonPrescriptionDrug
                  ? "Без рецепта"
                  : "По рецепту"}
              </Box>
              {productData.main_section.catalog_brand_slug && (
                <Link
                  sx={{
                    color: colors.blue2,
                    fontSize: { xs: "12px", tb: "14px" },
                  }}
                  href={createUrl([
                    citySlug,
                    paths.CATEGORY,
                    productData.main_section.catalog_brand_slug,
                  ])}
                  onClick={() =>
                    sendCustomYandexMetrik(
                      EnumMetrikActions.anotherProductFormBtnOnProductPage
                    )
                  }
                >
                  Все формы выпуска
                </Link>
              )}
            </Box>
          </Box>
          <Box display="flex" mb="4px">
            {isLoadingPrice ? (
              <Skeleton width="80%" height="45px" />
            ) : (
              <>
                {productPrice?.offers_amount === 0 ? (
                  <Typography
                    sx={{
                      fontSize: "24px",
                      color: colors.black2,
                      mb: 3,
                      fontWeight: 500,
                    }}
                  >
                    Нет в наличии
                  </Typography>
                ) : (
                  <>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { tb: "24px", xs: "18px" },
                        color: colors.black2,
                        fontWeight: 500,
                      }}
                    >
                      Цена
                    </Typography>
                    &nbsp;
                    <Typography
                      component="span"
                      sx={{
                        color: colors.blue2,
                        fontSize: { tb: "24px", xs: "18px" },
                        fontWeight: 500,
                      }}
                    >
                      {productPrice?.min_price === productPrice?.max_price
                        ? `от ${productPrice?.min_price} ₽`
                        : `от ${productPrice?.min_price} ₽ до ${productPrice?.max_price} ₽`}
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
          {isLoadingPrice ? (
            <Skeleton width="50%" height="35px" sx={{ mb: { xs: 2, tb: 3 } }} />
          ) : (
            productPrice &&
            productPrice?.offers_amount !== 0 && (
              <Typography
                component="div"
                sx={{
                  fontSize: { tb: "16px", xs: "14px" },
                  color: colors.gray3,
                  mb: { xs: 2, tb: 3 },
                  fontWeight: 400,
                }}
              >
                {`В наличии в ${
                  productPrice?.offers_amount
                } ${pluralPharmaciesAmount(productPrice?.offers_amount)}`}
              </Typography>
            )
          )}
          {productPrice?.offers_amount !== 0 && (
            <SubmitButton
              sx={{
                maxWidth: { xs: "296px", tb: "285px" },
                height: "48px",
                borderRadius: "8px",
                mb: { xs: 3, tb: 5 },
              }}
              typographySx={{
                color: colors.black2,
                fontWeight: 400,
                textTransform: "none",
                fontSize: "16px",
              }}
              onClick={() => {
                sendCustomYandexMetrik(
                  EnumMetrikActions.clickQuickOrderOnProductPage
                );
                setOpenReservationModal(true);
              }}
              title="Цены в аптеках"
            />
          )}

          <Box display="flex" flexDirection="column">
            <Typography
              component="span"
              sx={{
                mb: 2,
                color: colors.black,
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              Оплата и способы получения
            </Typography>
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: colors.blue2,
                  mr: "12px",
                }}
              />
              <Typography
                component="span"
                sx={{ color: colors.black, fontSize: "14px" }}
              >
                Только самовывоз
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: colors.blue2,
                  mr: "12px",
                }}
              />
              <Typography
                component="span"
                sx={{ color: colors.black, fontSize: "14px" }}
              >
                Оплата картой или наличными в аптеке
              </Typography>
            </Box>
          </Box>
        </Container>
        {!isLargeScreenDown && (
          <Box
            sx={{
              mb: 2,
            }}
          >
            <SidebarImage imgSrc={productData.main_section.image} />
            <PhotoCaption picMatch={productData.main_section.pic_match} />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default MainProductInfo;
